import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { TIMEFRAMES } from "@strat/format";
import type { RiskParams, StrategySpec, TradeDirection } from "@strat/format";
import type { WorkbenchOpen } from "@strat/ui";

import { api } from "../api";
import { money } from "../format";
import { useSession } from "../session";
import { Alert, Field } from "../shell/ui";
import { BotLeverageFields, MarketTypeToggle, useBotLeverage } from "./BotLeverage";
import type { BotMarketType } from "./BotLeverage";
import { Icon } from "./Icon";

/**
 * "Copy settings": everything needed to reproduce a backtest or a bot —
 * the rules, the market, the window, the capital — as one block of JSON on
 * the clipboard. For pasting into a note, a message, or another tool.
 */
export interface Settings {
  strategy: StrategySpec;
  symbol: string;
  timeframe: string;
  marketType: string;
  from?: number | null;
  to?: number | null;
  initialCapital: number;
  source?: string;
}

export function settingsJson(s: Settings): string {
  // The rules without identity: ids and timestamps belong to the original, not the copy.
  const { id: _id, createdAt: _c, updatedAt: _u, ...strategy } = s.strategy;
  return JSON.stringify(
    {
      kind: "strategyhive.settings",
      version: 1,
      market: { symbol: s.symbol, timeframe: s.timeframe, marketType: s.marketType },
      window: {
        from: s.from ? new Date(s.from).toISOString() : null,
        to: s.to ? new Date(s.to).toISOString() : null,
      },
      initialCapital: s.initialCapital,
      strategy,
      source: s.source ?? window.location.href,
    },
    null,
    2,
  );
}

export async function writeClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(text);
  // Plain-HTTP home servers have no Clipboard API: the old way still works there.
  const area = document.createElement("textarea");
  area.value = text;
  area.style.position = "fixed";
  area.style.opacity = "0";
  document.body.appendChild(area);
  area.select();
  document.execCommand("copy");
  area.remove();
}

export function CopySettingsButton({
  settings,
  label = "Copy settings",
}: {
  settings: Settings | null;
  label?: string;
}) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  const timer = useRef<number>();
  useEffect(() => () => window.clearTimeout(timer.current), []);
  if (!settings) return null;
  return (
    <button
      type="button"
      className={`btn btn--sm btn--ghost copy-btn${state === "copied" ? " is-done" : ""}`}
      title="Copy the rules, market, window and capital as JSON"
      onClick={() => {
        writeClipboard(settingsJson(settings)).then(
          () => setState("copied"),
          () => setState("failed"),
        );
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setState("idle"), 2200);
      }}
    >
      <Icon name={state === "copied" ? "check" : "copy"} />
      {state === "copied" ? "Copied" : state === "failed" ? "Couldn't copy" : label}
      <span className="sr-only" aria-live="polite">
        {state === "copied" ? "Settings copied to the clipboard" : ""}
      </span>
    </button>
  );
}

/** Open the Workbench on exactly these settings — to change something and run it again. */
export function useOpenInWorkbench() {
  const navigate = useNavigate();
  return (open: WorkbenchOpen) => navigate("/workbench", { state: { open } });
}

/**
 * "Run a copy" of a bot: the same rules as a paper bot of your own, on the
 * settings you confirm here. It is independent — it makes its own decisions
 * from the same rules, it does not mirror the original's trades.
 */
export function CopyBotDialog({
  bot,
  onClose,
}: {
  bot: {
    id: string;
    name: string;
    symbol: string;
    timeframe: string;
    startingCash: number;
    isOwner: boolean;
    /** The copy starts on the same kind of market: a futures bot's copy is a futures bot. */
    marketType?: string;
    /** The original's risk settings: the copy starts at its leverage and funding. */
    risk?: RiskParams;
    direction?: TradeDirection;
  };
  onClose: () => void;
}) {
  const { user } = useSession();
  const navigate = useNavigate();
  const [marketType, setMarketType] = useState<BotMarketType>(
    bot.marketType === "futures" ? "futures" : "spot",
  );
  const lev = useBotLeverage(bot.risk);
  const symbols = useQuery({
    queryKey: ["hub-symbols", marketType],
    queryFn: () =>
      api<{ symbols: string[] }>(`/data/symbols?marketType=${marketType}`).then((r) => r.symbols),
  });
  const [symbol, setSymbol] = useState(bot.symbol);
  // Fewer coins have futures than spot: switching keeps the coin when it can.
  const list = symbols.data;
  useEffect(() => {
    if (list && list.length > 0 && !list.includes(symbol)) setSymbol(list[0]!);
  }, [list, symbol]);
  const [timeframe, setTimeframe] = useState<string>(
    (TIMEFRAMES as readonly string[]).includes(bot.timeframe) ? bot.timeframe : "1h",
  );
  const [cash, setCash] = useState(bot.startingCash);
  const [visibility, setVisibility] = useState<"public" | "private">("public");
  const copy = useMutation({
    mutationFn: () =>
      api<{ id: string }>(`/hub/bots/${bot.id}/copy`, {
        method: "POST",
        body: {
          symbol,
          timeframe,
          marketType,
          startingCash: cash,
          visibility,
          ...lev.overrides(marketType),
        },
      }),
    onSuccess: (r) => navigate(`/b/${r.id}`),
  });
  const originalType = bot.marketType === "futures" ? "futures" : "spot";
  const same =
    symbol === bot.symbol &&
    timeframe === bot.timeframe &&
    cash === bot.startingCash &&
    marketType === originalType &&
    Object.keys(lev.overrides(marketType)).length === 0;
  return (
    <div className="dialog-backdrop" role="presentation" onClick={onClose}>
      <div
        className="dialog card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="copybot-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card__body stack">
          <h2 id="copybot-title">Run a copy of “{bot.name}”</h2>
          <p className="muted small">
            {bot.isOwner
              ? "A second bot with the same rules"
              : "The same rules become a strategy in your library, and a paper bot of yours starts running them"}{" "}
            — on the market below, with paper money. It makes its own decisions; it doesn't mirror this bot's
            trades.
          </p>
          <MarketTypeToggle value={marketType} onChange={setMarketType} />
          <div className="form-grid">
            <Field label="Market">
              <select value={symbol} onChange={(e) => setSymbol(e.target.value)}>
                {(symbols.data ?? [bot.symbol]).map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </Field>
            <Field label="Timeframe">
              <select value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
                {TIMEFRAMES.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </Field>
            <Field label="Paper cash">
              <input
                type="number"
                min={100}
                max={1_000_000}
                step={100}
                value={cash}
                onChange={(e) => setCash(Number(e.target.value))}
              />
            </Field>
            <Field label="Visibility">
              <select
                value={visibility}
                onChange={(e) => setVisibility(e.target.value as "public" | "private")}
              >
                <option value="public">Public — in the public bot ranking</option>
                <option value="private">Private — only you</option>
              </select>
            </Field>
          </div>
          <BotLeverageFields
            marketType={marketType}
            onMarketTypeChange={setMarketType}
            state={lev}
            direction={bot.direction ?? "long"}
            margin={bot.risk?.sizeMode === "pct_equity" ? (cash * bot.risk.sizeValue) / 100 : null}
            stopLossPct={bot.risk?.stopLossPct}
          />
          <p className="small muted">
            {same
              ? `Same settings as the original: ${bot.symbol} ${bot.timeframe}, ${money(bot.startingCash)} paper cash.`
              : `Changed from the original (${bot.symbol} ${originalType} ${bot.timeframe}, ${money(bot.startingCash)}, ${lev.strategyLeverage}×).`}
            {user?.role === "starter" && " Starter accounts keep one bot."}
          </p>
          {copy.error && <Alert tone="error">{copy.error.message}</Alert>}
          <div className="row row--end">
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={copy.isPending}
              onClick={() => copy.mutate()}
            >
              {copy.isPending ? "Starting…" : "Start my copy"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
